import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { driveReadinessApi } from "../../services/driveReadinessApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function DriveIngestionPage() {
  const { t } = useLocalization();
  const { canManageDriveIngestion } = usePermissions();
  const [health, setHealth] = useState(null);
  const [months, setMonths] = useState([]);
  const [newMonth, setNewMonth] = useState("");

  const load = useCallback(() => {
    driveReadinessApi.health().then((r) => setHealth(r.data)).catch(() => {});
    driveReadinessApi.listMonths().then((r) => setMonths(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (canManageDriveIngestion) load(); }, [canManageDriveIngestion, load]);

  if (!canManageDriveIngestion) return <p className="text-sm text-gray-500" data-testid="drive-access-denied">{t("common.accessDenied")}</p>;

  const scan = async (month) => {
    try { await driveReadinessApi.scanMonth(month); toast.success(`${t("mgmtDrive.scanTriggeredFor")} ${month}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const lock = async (month) => {
    try { await driveReadinessApi.lockMonth(month); toast.success(`${month} ${t("mgmtDrive.lockedForImport")}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="drive-ingestion-page">
      <PageHelpBanner pageKey="drive-ingestion" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtDrive.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtDrive.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtDrive.subtitle")}</p>
      </div>

      {health && (
        <div className={`rounded-xl border p-4 flex items-center justify-between ${health.status === "CONNECTED" ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-gray-50"}`} data-testid="drive-health-panel">
          <div>
            <p className="text-sm font-semibold" data-testid="drive-health-status">{health.status}</p>
            <p className="text-xs text-gray-500">{health.detail}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex gap-3" data-testid="scan-new-month-panel">
        <input data-testid="new-month-input" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} placeholder="YYYY-MM" className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
        <button data-testid="scan-new-month-btn" onClick={() => newMonth && scan(newMonth)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtDrive.scanMonth")}</button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtDrive.month")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtDrive.files")}</th><th className="px-4 py-3 font-medium">{t("mgmtDrive.lastScan")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.payroll_month} className="border-t border-gray-100" data-testid={`drive-month-row-${m.payroll_month}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{m.payroll_month}</td>
                <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-2 text-gray-600">{m.files_count}</td>
                <td className="px-4 py-2 text-gray-500">{m.last_scan_at || "-"}</td>
                <td className="px-4 py-2 space-x-2">
                  {m.status !== "LOCKED" && <button data-testid={`rescan-btn-${m.payroll_month}`} onClick={() => scan(m.payroll_month)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtDrive.rescan")}</button>}
                  {m.status !== "LOCKED" && <button data-testid={`lock-btn-${m.payroll_month}`} onClick={() => lock(m.payroll_month)} className="text-xs font-semibold text-red-600 hover:underline">{t("mgmtDrive.lock")}</button>}
                </td>
              </tr>
            ))}
            {months.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-drive-months-row">{t("mgmtDrive.noMonthsScanned")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
